/**
 * SessionControls - session history + new-session controls for the AI chat
 * right pane header, styled after the Claude Code VSCode extension:
 * the pane header shows the active session title on the left (via the rail
 * item's headerTitle) and these two icon buttons on the right - a history
 * button opening the session list and a new-session button.
 *
 * All session state and handlers are received as props from useChatSession,
 * which is owned at the page level (ProjectPage / DealWorkspace / AppShell).
 */

import {
  CounterClockwiseClockIcon,
  Cross1Icon,
  DropdownMenu,
  Pencil1Icon,
  PlusIcon,
  Tooltip,
} from '@protoss/ui';
import * as React from 'react';
import { formatDateShort, formatTime } from '../../../lib/datetime';
import type { ChatSessionSummary, SessionSlice, SessionsStatus } from '../state/chatState';

/** Format a date string as a human-readable subtitle, e.g. "Today at 7:29 PM" */
function formatSubtitle(dateStr: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const time = formatTime(dateStr);
  if (d.toDateString() === today.toDateString()) return `Today at ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday at ${time}`;
  return `${formatDateShort(dateStr)} at ${time}`;
}

export interface SessionTitleEditableProps {
  sessions: ChatSessionSummary[];
  activeSessionUuid: string | null;
  onRename: (sessionUuid: string, title: string) => void;
}

/**
 * SessionTitleEditable - the pane-header title for the active session, with
 * inline rename. Shows "Untitled" until the AI-inferred title arrives
 * (session_title_updated). Hovering reveals a pencil icon; clicking swaps the
 * text for an input in place (same height/font/padding in both modes, and the
 * pencil slot is always reserved, so there is no flicker or layout shift).
 * Clicking outside (input blur) or pressing Enter saves; the X button or
 * Escape cancels. Empty input is treated as cancel.
 */
export function SessionTitleEditable({
  sessions,
  activeSessionUuid,
  onRename,
}: SessionTitleEditableProps) {
  const activeSession = sessions.find((s) => s.uuid === activeSessionUuid);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  // Leaving the active session (switch/new) while editing discards the draft.
  React.useEffect(() => {
    setEditing(false);
  }, [activeSessionUuid]);

  const startEdit = () => {
    if (!activeSession) return;
    setDraft(activeSession.title ?? '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (!activeSession || !next || next === (activeSession.title ?? '')) return;
    onRename(activeSession.uuid, next);
  };

  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <span className="ai-chat-session-title ai-chat-session-title--editing">
        <input
          ref={inputRef}
          className="ai-chat-session-title__input"
          value={draft}
          placeholder="Untitled"
          aria-label="Session title"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              inputRef.current?.blur(); // blur commits
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
        />
        <Tooltip.Simple label="Cancel">
          <button
            type="button"
            className="ai-chat-session-title__cancel"
            aria-label="Cancel rename"
            // mousedown (not click): preventDefault stops the input blur, so the
            // blur-commit never races the cancel.
            onMouseDown={(e) => {
              e.preventDefault();
              cancel();
            }}
          >
            <Cross1Icon aria-hidden="true" />
          </button>
        </Tooltip.Simple>
      </span>
    );
  }

  return (
    <Tooltip.Simple label={activeSession ? 'Rename session' : null}>
      <button
        type="button"
        className="ai-chat-session-title"
        onClick={startEdit}
        disabled={!activeSession}
      >
        <span className="ai-chat-session-title__text">{activeSession?.title ?? 'Untitled'}</span>
        <Pencil1Icon className="ai-chat-session-title__edit-icon" aria-hidden="true" />
      </button>
    </Tooltip.Simple>
  );
}

export interface SessionControlsProps {
  sessions: ChatSessionSummary[];
  sessionsStatus: SessionsStatus;
  activeSessionUuid: string | null;
  sessionPool: Record<string, SessionSlice>;
  /** Called when the history trigger is opened to lazily fetch the sessions list */
  onLoad: () => void;
  onSwitch: (sessionUuid: string) => void;
  onCreate: () => void;
}

export function SessionControls({
  sessions,
  sessionsStatus,
  activeSessionUuid,
  sessionPool,
  onLoad,
  onSwitch,
  onCreate,
}: SessionControlsProps) {
  // Derive which background session UUIDs have an in-flight run.
  const backgroundRunning = new Set(
    Object.entries(sessionPool)
      .filter(([uuid, slice]) => uuid !== activeSessionUuid && slice.activeRunId != null)
      .map(([uuid]) => uuid)
  );
  const anyBackgroundRunning = backgroundRunning.size > 0;

  // Derive which background session UUIDs have an unread completed response.
  const backgroundUnread = new Set(
    Object.entries(sessionPool)
      .filter(([uuid, slice]) => uuid !== activeSessionUuid && slice.hasUnread)
      .map(([uuid]) => uuid)
  );
  const anyBackgroundUnread = backgroundUnread.size > 0;

  return (
    <div className="ai-chat-session-controls">
      <DropdownMenu.Root>
        <Tooltip.Simple label="Session history">
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="ai-chat-session-iconbtn"
              data-running={anyBackgroundRunning ? 'true' : 'false'}
              data-unread={anyBackgroundUnread ? 'true' : 'false'}
              aria-label="Session history"
              onClick={onLoad}
            >
              <CounterClockwiseClockIcon aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>
        </Tooltip.Simple>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="ai-chat-session-menu dropdown-content"
            sideOffset={4}
            align="end"
          >
            <div className="ai-chat-session-menu__list">
              {sessions.map((sess) => {
                const displayDate = sess.lastMessageAt ?? sess.createdAt;
                return (
                  <DropdownMenu.Item
                    key={sess.uuid}
                    className="ai-chat-session-menu__item dropdown-item"
                    data-active={sess.uuid === activeSessionUuid ? 'true' : 'false'}
                    data-running={backgroundRunning.has(sess.uuid) ? 'true' : 'false'}
                    data-unread={backgroundUnread.has(sess.uuid) ? 'true' : 'false'}
                    onSelect={() => onSwitch(sess.uuid)}
                  >
                    <span className="ai-chat-session-menu__title">{sess.title ?? 'Untitled'}</span>
                    <span className="ai-chat-session-menu__subtitle">
                      {formatSubtitle(displayDate)}
                    </span>
                  </DropdownMenu.Item>
                );
              })}

              {sessionsStatus === 'loading' && (
                <DropdownMenu.Item
                  disabled
                  className="ai-chat-session-menu__item dropdown-item ai-chat-session-menu__item--hint"
                >
                  Loading…
                </DropdownMenu.Item>
              )}

              {sessionsStatus === 'loaded' && sessions.length === 0 && (
                <DropdownMenu.Item
                  disabled
                  className="ai-chat-session-menu__item dropdown-item ai-chat-session-menu__item--hint"
                >
                  No sessions yet
                </DropdownMenu.Item>
              )}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <Tooltip.Simple label="New session">
        <button
          type="button"
          className="ai-chat-session-iconbtn"
          aria-label="New session"
          onClick={onCreate}
        >
          <PlusIcon aria-hidden="true" />
        </button>
      </Tooltip.Simple>
    </div>
  );
}
